import axios, { AxiosResponse } from "axios";
import {
  ApplicationUser,
  File as DrsFile,
  FileFieldsFragment,
  Tenant,
  ToolVersion,
} from "graphql/_Types";
import { getEnvVar } from "ideas.env";
import { useCallback } from "react";
import { useUserContext } from "providers/UserProvider/UserProvider";
import { FileSource, FileStatus, ProcessingStatus } from "types/constants";
import { getRequestHeaders } from "utils/getRequestHeaders";
import { JsonValue } from "type-fest";

type CreateFileInput = Pick<
  DrsFile,
  "id" | "projectId" | "name" | "tenantId"
> & {
  processingStatus: ProcessingStatus;
  fileType: NonNullable<DrsFile["fileType"]>;
  fileFormat: NonNullable<DrsFile["fileFormat"]>;
  size: number;
};
type CreateCodeFileInput = {
  /**
   * TODO: API types
   * Graphql persisted queries removed the code file model from the
   * schema/types because it's not exposed to graphQL..  Previously
   * we could do Pick<CodeFile, "id"|"name"|"tenantId"> so the types were
   * linked to the model rather than hardcoded on the frontend. This is already
   * something I'm thinking about as part of the django frontend client work
   * and elimination of graphql entirely
   */
  id: string;
  name: string;
  tenantId: number;
  size: number;
  bundle_metadata: JsonValue;
  tool_version: ToolVersion["id"];
  user: ApplicationUser["id"];
  checksum: string;
};

type CreateFileBody = {
  id: DrsFile["id"];
  name: DrsFile["name"];
  user: DrsFile["userId"];
  tenant: DrsFile["tenantId"];
  part_size: DrsFile["partSize"];
  size: DrsFile["size"];
  date_created: DrsFile["dateCreated"];
  project: DrsFile["projectId"];
  uploaded: true;
  processing_status: ProcessingStatus;
  file_type: DrsFile["fileType"];
  file_format: DrsFile["fileFormat"];
};

type CreateCodeFileBody = {
  id: DrsFile["id"];
  name: DrsFile["name"];
  user: DrsFile["userId"];
  tenant: DrsFile["tenantId"];
  part_size: DrsFile["partSize"];
  size: DrsFile["size"];
  date_created: DrsFile["dateCreated"];
  tool_version: ToolVersion["id"];
  bundle_metadata: JsonValue;
  uploaded: true;
  checksum: string;
};

type CreateFileResponse = AxiosResponse<{
  date_created: DrsFile["dateCreated"];
  id: DrsFile["id"];
  reference_id: DrsFile["referenceId"];
  name: DrsFile["name"];
  project: DrsFile["projectId"];
  part_size: NonNullable<DrsFile["partSize"]>;
  size: DrsFile["size"];
  status: FileStatus;
  user: DrsFile["userId"];
  tenant: DrsFile["tenantId"];
  processing_status: ProcessingStatus;
  file_type: DrsFile["fileType"];
  file_format: DrsFile["fileFormat"];
}>;

type CreateCodeFileResponse = AxiosResponse<{
  id: string;
  name: string;
  location: null;
  size: number;
  part_size: number;
  user: ApplicationUser["id"];
  tenant: Tenant["id"];
  date_created: string;
  status: FileStatus;
  file_tree: null;
}>;

/**
 * Calculates a multipart upload part size for a minimum part size of 100 MB
 * (AWS guideline) and maximum 10,000 parts (AWS limit). Part sizes will only
 * increase from the minimum for extremely large (1TB+) files.
 * @param fileSize file size in bytes
 * @returns A multipart upload part size in bytes
 */
const getPartSize = (fileSize: number) => {
  const minPartSize = 52_428_800; // 50 MB
  const maxNumParts = 10_000;
  const factor = Math.ceil(fileSize / (minPartSize * maxNumParts));
  return factor * minPartSize;
};

/**
 * Formats a filename to be compatible with the backend
 * @param filename
 * @returns The formatted filename
 */
const formatFilename = (filename: string) => {
  /*
      A valid filename may contain the following characters:
      - Letters (A-Za-z)
      - Numbers (\d)
      - Periods (.)
      - Hyphens (-)
      - Underscores (_)
    */
  const invalidCharRegExp = /[^A-Za-z\d.-_]/g;
  const replaceValue = "-";
  return filename.replace(invalidCharRegExp, replaceValue);
};

export const useCreateFileDjango = () => {
  const currentUser = useUserContext((s) => s.currentUser);

  const createFile = useCallback(
    async (input: CreateFileInput) => {
      const url = getEnvVar("URL_DRS_FILE_CREATE");

      const body: CreateFileBody = {
        id: input.id,
        name: formatFilename(input.name),
        user: currentUser.id,
        tenant: input.tenantId,
        size: input.size.toString(),
        part_size: getPartSize(input.size),
        date_created: new Date().toISOString(),
        project: input.projectId,
        uploaded: true,
        processing_status: input.processingStatus,
        file_format: input.fileFormat,
        file_type: input.fileType,
      };

      const headers = await getRequestHeaders({ tenantId: input.tenantId });

      const { data } = await axios.post<CreateFileBody, CreateFileResponse>(
        url,
        body,
        { headers },
      );

      const file = {
        __typename: "File",
        id: data.id,
        referenceId: data.reference_id,
        key: "",
        name: data.name,
        dateCreated: data.date_created,
        dateDeleted: null,
        tenantId: data.tenant,
        userId: data.user,
        status: FileStatus.CREATED,
        location: null,
        size: data.size,
        partSize: data.part_size,
        fileFormat: data.file_format,
        fileStructure: null,
        fileType: data.file_type,
        multipartUploadId: null,
        uploadStatus: null,
        signal: null,
        parentIds: null,
        projectId: data.project,
        originalName: data.name,
        dateArchived: null,
        dateDataDeleted: null,
        seriesParentId: null,
        source: FileSource.UPLOADED,
        isSeries: false,
        seriesOrder: null,
        processingStatus: data.processing_status,
        tags: [],
      } satisfies FileFieldsFragment;

      return { file };
    },
    [currentUser.id],
  );

  return { createFile };
};

export const createCodeFile = async (input: CreateCodeFileInput) => {
  const url = getEnvVar("URL_DRS_CODE_FILE");

  const body: CreateCodeFileBody = {
    id: input.id,
    name: input.name,
    user: input.user,
    tenant: input.tenantId,
    size: input.size.toString(),
    part_size: getPartSize(input.size),
    date_created: new Date().toISOString(),
    uploaded: true,
    tool_version: input.tool_version,
    bundle_metadata: input.bundle_metadata,
    checksum: input.checksum,
  };

  const headers = await getRequestHeaders({ tenantId: input.tenantId });

  const { data } = await axios.post<CreateFileBody, CreateCodeFileResponse>(
    url,
    body,
    { headers },
  );

  return data;
};
